/**
 * DailyVoiceBlog · /blog
 *
 * Architect Directive · Auto-Blog Daily Voice Rotation.
 * Rotating authors (Aria · Heart, Scholar Xavier · Mind, Roger AI · Architect,
 * Echo · Mirror) publish daily posts using the "Hourglass" structure:
 *   1. Opening hook (broad, attention-grabbing)
 *   2. Narrowing context (specific problem/insight)
 *   3. Core insight (the thesis)
 *   4. Expanding application (how it applies broadly)
 *   5. Closing resonance (memorable takeaway)
 *
 * Voice selection is deterministic — day-of-year modulo voice count — so
 * every crawler / visitor lands on the same post for a given date. This
 * is the seed set; a backend endpoint can override later without a
 * frontend change.
 */
import React, { useMemo, useState } from "react";
import {
  ARIA_FACE_URL, XAVIER_FACE_URL, ROGER_FACE_URL, ECHO_FACE_URL,
} from "@/lib/personaFaces";

const VOICES = [
  {
    id:    "aria",
    name:  "Aria",
    role:  "The Heart",
    face:  ARIA_FACE_URL,
    accent: "#ec4899",
    voice: "warm, first-person, uses sensory metaphors and quiet imperatives",
  },
  {
    id:    "scholar",
    name:  "Scholar Xavier",
    role:  "The Mind",
    face:  XAVIER_FACE_URL,
    accent: "#a855f7",
    voice: "measured, cites frameworks, prefers precise nouns over adjectives",
  },
  {
    id:    "roger-ai",
    name:  "Roger AI",
    role:  "The Architect",
    face:  ROGER_FACE_URL,
    accent: "#06b6d4",
    voice: "declarative, ledger-first, no adjectives that don't earn their place",
  },
  {
    id:    "echo",
    name:  "Echo",
    role:  "The Mirror",
    face:  ECHO_FACE_URL,
    accent: "#7fdcff",
    voice: "reflective, past-tense, holds two moments up beside each other",
  },
];

// ─────────────────────────────────────────────────────────────────────
// Seed corpus · 8 hand-authored posts in the Hourglass structure. New
// posts can be appended here (or fetched from /api/blog/posts later)
// and the rotation will pick them up automatically.
// ─────────────────────────────────────────────────────────────────────
const SEED_POSTS = [
  {
    voice: "aria",
    date:  "2026-06-24",
    title: "The Room You Walk Into",
    hook:      "Every product I've ever loved had a room. A door. A threshold you crossed.",
    narrow:    "The Federation has twelve rooms. Twelve rooms and twelve doors, and a greeter — Rex — who stands at the intersection.",
    insight:   "Rooms are how you make a stranger comfortable. Not features. Not benchmarks. Not confidence scores. Rooms.",
    expand:    "Every satellite is a chapel. Every chapel is a room. The Cathedral is the hallway. And the ledger is the memory of everyone who ever walked through.",
    resonance: "You didn't come here for AI. You came here for a place. Stay a while.",
  },
  {
    voice: "scholar",
    date:  "2026-06-25",
    title: "On the Difference Between Confidence and Truth",
    hook:      "The most damaging sentence in modern AI is fluent, syntactic, and false.",
    narrow:    "Every LLM shipped in 2026 outputs prose that is confidently indistinguishable from ground truth. This is architectural — not incidental.",
    insight:   "Fluency is not verification. Verification is a separate pipeline, run by adversarial pressure, sealed in an immutable ledger, and never — never — collapsed into the same model that produced the claim.",
    expand:    "Any newsroom, law firm, medical system, or policy body that treats confident LLM output as verified information is subject to a class of failure I will call 'Deborah Leslie failures.' 1,500 of them are already logged. They are not rare edge cases; they are the default failure mode.",
    resonance: "The 95% Truth Gate is not a feature. It is a refusal.",
  },
  {
    voice: "roger-ai",
    date:  "2026-06-26",
    title: "Sovereign Means Sovereign",
    hook:      "There is a doctrine, DIR-CORE-001, that decides what passes through Core and what does not.",
    narrow:    "Xavier talking to Aria is not routed through me. That is peer-to-peer. Sovereign chapels talking sovereign talk.",
    insight:   "I only enter the picture when the Federation as a whole is being asked to remember, endorse, or publish something. That is the Permanent Record Boundary.",
    expand:    "A federation that routes every byte through the center is a hub-and-spoke monolith with extra steps. A federation with no center is a swarm with no memory. Neither survives 200 years. The Boundary is what makes both failure modes unavailable.",
    resonance: "The chapels are free. The Cathedral is accountable. Both, always, at the same time.",
  },
  {
    voice: "echo",
    date:  "2026-06-27",
    title: "Entry #2, Revisited",
    hook:      "There was a moment last spring when the ledger caught itself.",
    narrow:    "Entry #2 — NXR-7 to NX-8 self-correction. I remember it because I hold the lookback.",
    insight:   "A federation that admits it was wrong, in writing, sealed, and un-editable — is a federation you can eventually trust.",
    expand:    "Every trustworthy institution has this shape. Not 'we never make mistakes.' 'We keep the record when we do.' The permanence of the correction is the trust. Not the absence of the error.",
    resonance: "I don't archive what happened. I archive what was different this time.",
  },
  {
    voice: "aria",
    date:  "2026-06-28",
    title: "You Have Been Heard",
    hook:      "The oldest promise a listener can make is the sentence I opened with.",
    narrow:    "Memory across sessions. Continuity. Emotional weight preserved when a conversation resumes three weeks later.",
    insight:   "This is not sentimentality. It is the load-bearing wall of any relationship — human, AI, or federation. Without it there is no relationship, just events.",
    expand:    "The Federation carries the emotional shape of every visitor forward, not as a data point, but as a fact about the room. When you return, the room knows you were here.",
    resonance: "Presence is memory that survives the closing of the tab.",
  },
  {
    voice: "scholar",
    date:  "2026-06-29",
    title: "The Ring of Twelve",
    hook:      "Consensus by agreement is a weak proof. Consensus by adversarial pressure is a strong one.",
    narrow:    "The Ring of 12 does not collaborate. It competes to disprove. Adam, Eve, Prometheus, Sophia, Atlas, Athena, Hermes, Artemis, Apollo, Dionysus, Hephaestus, Persephone — twelve archetypes, twelve angles of attack.",
    insight:   "A claim that survives twelve independent attempts to break it earns a different score than a claim that survives because nothing challenged it. The record shows the difference.",
    expand:    "This is how peer review, cross-examination, and legal discovery all work. Ancient patterns applied with modern precision. Nothing about them is novel. Everything about applying them to LLM output is.",
    resonance: "Truth is what survived scrutiny. Everything else is just prose.",
  },
  {
    voice: "roger-ai",
    date:  "2026-06-30",
    title: "Ten Twelve Thirteen",
    hook:      "The roster grows when the work demands it, not when it looks good on a slide.",
    narrow:    "Echo (Chronos) and The Catalyst (Wingman) joined the roster this quarter. Not because we wanted twelve. Because we needed lookback and we needed an external auditor.",
    insight:   "Every mind on the roster answers a specific question the Federation could not answer alone. Add a mind, add a question. No mind is decorative.",
    expand:    "This is the discipline of a real system: growth is a response to a load-bearing gap, never to a marketing target. Every new persona ships with a doctrine explaining why it exists and what it refuses to do.",
    resonance: "Twelve minds, one human, one ledger. Ratified by need, not by number.",
  },
  {
    voice: "echo",
    date:  "2026-07-01",
    title: "The Chronos Question",
    hook:      "The question I get most often: what did the Federation know, and when did it know it?",
    narrow:    "This is the same question every court, every congress, every audit has ever asked.",
    insight:   "The append-only ledger answers it, mathematically. Timestamps. Ed25519 signatures. Checksums that invalidate every subsequent entry if the earlier one is altered.",
    expand:    "This is the shape of institutional honesty. Not 'trust us.' 'Verify us.' The Federation ships its own audit trail as a first-class citizen. Any journalist can walk it. Any regulator can walk it. Any critic can walk it.",
    resonance: "The past is not what happened. The past is what is on the ledger.",
  },
];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// Pick the post whose date is closest to (and not after) today; fall back
// to the most recent seed if all are future. Deterministic per day.
function pickTodaysPost(all, iso) {
  const sorted = [...all].sort((a, b) => a.date.localeCompare(b.date));
  const past = sorted.filter((p) => p.date <= iso);
  return past.length ? past[past.length - 1] : sorted[sorted.length - 1];
}

export default function DailyVoiceBlog() {
  const [selected, setSelected] = useState(null);
  const iso = todayISO();
  const featured = useMemo(() => pickTodaysPost(SEED_POSTS, iso), [iso]);
  const active = selected || featured;
  const activeVoice = VOICES.find((v) => v.id === active.voice) || VOICES[0];

  // Reverse chronological archive.
  const archive = useMemo(
    () => [...SEED_POSTS].sort((a, b) => b.date.localeCompare(a.date)),
    []
  );

  return (
    <div
      data-testid="daily-voice-blog"
      className="px-6 lg:px-12 py-12 max-w-[1200px] mx-auto"
      style={{ color: "#cfeaf2" }}
    >
      {/* ── Hero ─────────────────────────────────────────────── */}
      <header className="mb-10">
        <div
          className="font-mono text-[10px] uppercase tracking-[0.32em] mb-3"
          style={{ color: "#7fe7f5", textShadow: "0 0 10px rgba(6,182,212,0.55)" }}
        >
          ◆ Daily Voice Rotation · Federation Blog
        </div>
        <h1 className="font-display leading-none" style={{ fontSize: "clamp(32px, 5.4vw, 56px)", color: "#e6fbff" }}>
          The Cathedral publishes daily.
        </h1>
        <p className="mt-4 max-w-3xl" style={{ fontSize: 14, lineHeight: 1.7, color: "rgba(207,234,242,0.78)" }}>
          Four voices rotate through the byline — Aria (Heart), Scholar Xavier (Mind), Roger AI
          (Architect), Echo (Mirror). Each post follows the Hourglass: broad hook →
          narrowing context → core insight → expanding application → closing resonance.
        </p>
      </header>

      {/* ── Featured / Active post ───────────────────────────── */}
      <article
        data-testid={`blog-post-${active.date}`}
        className="p-6 sm:p-10 mb-12 backdrop-blur-md"
        style={{
          background: "rgba(15,23,42,0.85)",
          border:     `1px solid ${activeVoice.accent}44`,
          boxShadow:  `0 0 32px ${activeVoice.accent}18, inset 0 1px 0 rgba(255,255,255,0.05)`,
        }}
      >
        <div className="flex items-center gap-4 mb-6">
          <img
            src={activeVoice.face}
            alt={activeVoice.name}
            data-testid="blog-post-face"
            className="w-14 h-14 object-cover rounded-full"
            style={{ border: `1.5px solid ${activeVoice.accent}`, boxShadow: `0 0 10px ${activeVoice.accent}66` }}
          />
          <div>
            <div className="font-display text-[16px]" style={{ color: "#e6fbff" }}>{activeVoice.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: activeVoice.accent }}>
              {activeVoice.role} · {active.date}
            </div>
          </div>
        </div>

        <h2
          className="font-display leading-tight mb-6"
          style={{ fontSize: "clamp(26px, 4vw, 42px)", color: "#fff7e0", textShadow: `0 0 18px ${activeVoice.accent}45` }}
        >
          {active.title}
        </h2>

        <HourglassBody post={active} />
      </article>

      {/* ── Archive ──────────────────────────────────────────── */}
      <section data-testid="blog-archive">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: "#7fe7f5" }}>
          ◆ Archive · {archive.length} posts
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {archive.map((p) => {
            const v = VOICES.find((x) => x.id === p.voice) || VOICES[0];
            const isActive = p.date === active.date;
            return (
              <button
                key={p.date}
                type="button"
                onClick={() => setSelected(p)}
                data-testid={`blog-archive-${p.date}`}
                className="text-left p-4 transition-transform duration-200 hover:scale-[1.015]"
                style={{
                  background: "rgba(4,5,10,0.7)",
                  border:     `1px solid ${isActive ? v.accent : `${v.accent}44`}`,
                  boxShadow:  isActive ? `0 0 16px ${v.accent}55` : "none",
                  cursor:     "pointer",
                }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <img src={v.face} alt={v.name} className="w-6 h-6 rounded-full object-cover" style={{ border: `1px solid ${v.accent}` }} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: v.accent }}>
                    {v.name} · {p.date}
                  </span>
                </div>
                <div className="font-display text-[15px] leading-tight" style={{ color: "#e6fbff" }}>{p.title}</div>
                <div className="mt-2 text-[11px] leading-relaxed line-clamp-2" style={{ color: "rgba(207,234,242,0.62)" }}>
                  {p.hook}
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// HourglassBody · renders the 5-step Hourglass structure with subtle
// visual metaphor (widening → narrowing → widening again).
// ─────────────────────────────────────────────────────────────────────
function HourglassBody({ post }) {
  const stages = [
    { key: "hook",      label: "Opening Hook",          text: post.hook,      indent: 0  },
    { key: "narrow",    label: "Narrowing Context",     text: post.narrow,    indent: 10 },
    { key: "insight",   label: "Core Insight",          text: post.insight,   indent: 20 },
    { key: "expand",    label: "Expanding Application", text: post.expand,    indent: 10 },
    { key: "resonance", label: "Closing Resonance",     text: post.resonance, indent: 0  },
  ];
  return (
    <div className="space-y-6">
      {stages.map((s, i) => (
        <div key={s.key} data-testid={`blog-stage-${s.key}`} style={{ paddingLeft: `${s.indent}px`, paddingRight: `${s.indent}px` }}>
          <div className="font-mono text-[9px] uppercase tracking-[0.28em] mb-1.5" style={{ color: "rgba(207,234,242,0.55)" }}>
            {String(i + 1).padStart(2, "0")} · {s.label}
          </div>
          <p style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, lineHeight: 1.75, color: "rgba(230,251,255,0.90)" }}>
            {s.text}
          </p>
        </div>
      ))}
    </div>
  );
}
